import { useId, useState, useMemo } from "react";
import { RefreshCw, Settings } from "lucide-react";

type PreviewMode = "file" | "dev";

interface Props {
  projectId: string;
  filePath: string;
  devServerUrl: string | null;
  initialMode?: PreviewMode;
  onConfigureDevServer?: () => void;
}

export function PreviewPane({
  projectId,
  filePath,
  devServerUrl,
  initialMode = "file",
  onConfigureDevServer,
}: Props) {
  const [mode, setMode] = useState<PreviewMode>(initialMode);
  const [refreshKey, setRefreshKey] = useState(0);
  // Instance-scoped radio group: a static name would group the File/Dev radios
  // across EVERY mounted PreviewPane (EditorFrame keeps one per open file), so
  // toggling one pane could uncheck another's radio. useId() is unique per
  // instance and stable across renders. (Gemini + Codex.)
  const radioGroupName = useId();

  const iframeSrc = useMemo(() => {
    if (mode === "file") {
      // Encode each path segment so spaces and unicode survive the round trip.
      const encodedPath = filePath
        .split("/")
        .map((s) => encodeURIComponent(s))
        .join("/");
      return `/api/preview/file/${encodeURIComponent(projectId)}/${encodedPath}?r=${refreshKey}`;
    }
    if (devServerUrl) {
      return `/api/preview/dev/${encodeURIComponent(projectId)}/?r=${refreshKey}`;
    }
    return null;
  }, [mode, projectId, filePath, devServerUrl, refreshKey]);

  // new URL(devServerUrl) throws TypeError on invalid input — guard so the
  // pane never crashes when a project's stored URL is malformed.
  let devHostLabel = "";
  if (devServerUrl) {
    try {
      devHostLabel = new URL(devServerUrl).host;
    } catch {
      devHostLabel = devServerUrl;
    }
  }

  return (
    <div className="preview-pane">
      <div className="preview-pane__toolbar">
        <label className="preview-pane__radio">
          <input
            type="radio"
            name={radioGroupName}
            checked={mode === "file"}
            onChange={() => setMode("file")}
          />
          File: {filePath}
        </label>
        <label className="preview-pane__radio">
          <input
            type="radio"
            name={radioGroupName}
            checked={mode === "dev"}
            onChange={() => setMode("dev")}
          />
          Dev{devHostLabel ? `: ${devHostLabel}` : ""}
        </label>
        <div className="preview-pane__toolbar-spacer" />
        {mode === "dev" && (
          <button
            type="button"
            className="preview-pane__btn"
            aria-label="Configure dev server"
            onClick={() => onConfigureDevServer?.()}
          >
            <Settings size={14} />
          </button>
        )}
        <button
          type="button"
          className="preview-pane__btn"
          aria-label="Refresh preview"
          onClick={() => setRefreshKey((k) => k + 1)}
        >
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="preview-pane__body">
        {iframeSrc ? (
          <iframe
            key={iframeSrc}
            title="Preview"
            src={iframeSrc}
            // Opaque origin — see the note in BrowserPane.tsx. This pane frames
            // the same /api/preview/* routes, so it had the same hole: project
            // HTML running with the app's origin.
            sandbox="allow-scripts"
            className="preview-pane__iframe"
          />
        ) : (
          <div className="preview-pane__empty">
            <p>Where is your dev server running?</p>
            <p>Configure it in the project settings or click the gear icon above.</p>
          </div>
        )}
      </div>
    </div>
  );
}
